/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import { newUID } from '../utils/tools';
import {
    EntityStates,
    EntityTypes
} from "../utils/dvtypes";
import { Dictionary } from "../utils/Dictionary";

/**
 * Allow for expansion of entity and entity definition objects by being able to accommodate properties not yet known
 * @param {EntityBase|EntityDefinitionBase|Object} entity
 * @param {Object} defaults
 */
export function applyUnknownProperties (entity, defaults) {
    for (let i in defaults) {
        if (defaults.hasOwnProperty(i)) {
            // only add this property if the default's value is not null and the entity object's is...
            if (defaults[i] != null && entity[i] == null) {
                entity[i] = defaults[i];
            }
        }
    }
}

/**
 * Return the property value if it is not undefined or null; otherwise return the default value
 * @param {*} [propertyValue]
 * @param {*|Function} [defaultValue] - If a function, will execute with no parameters to return the value
 * @returns {*}
 */
export function setPropertyOrDefault (propertyValue, defaultValue) {
    if (propertyValue === undefined || propertyValue === null) {
        if (typeof defaultValue === 'function') {
            return defaultValue();
        }
        else {
            return defaultValue;
        }
    }

    return propertyValue;
}

export class EntityDefinitionBase {
    /**
     * Create a new instance of a entity definition object
     * @param {Object} [defaults = {}] - (OPTIONAL) Default object (transmitted definition object?) to use for properties
     * @constructor
     */
    constructor (defaults = {}) {
        /**
         * The description for this definition instance
         * @type {string}
         */
        this.Description = defaults.Description || '';

        /**
         * The name for this definition instance
         * @type {string}
         */
        this.Name = defaults.Name || '';

        /**
         * The version of the client when this definition was created/modified
         */
        this.ClientVersion = defaults.ClientVersion || app.version;

        /**
         * The current version of the object for this definition
         * @type {string}
         */
        this.CurrentVersion = defaults.CurrentVersion || "::0";

        /**
         * The version of the object when this was originally created (cloned?)
         * @type {string}
         */
        this.OriginalVersion = defaults.OriginalVersion || "::0";

        /**
         * ID of the user that created this instance
         * @type {string}
         */
        this.UserCreated = defaults.UserCreated || "";

        /**
         * ID of the user that last modified this instance
         * @type {string}
         */
        this.UserLastModified = defaults.UserLastModified || "";
    }
}

export class EntityBase {
    /**
     * Create a base entity object.
     * @param {Object} [defaults = {}] - (OPTIONAL) Default object (transmitted entity object?) to use for properties
     * @class
     * @classdesc Base Entity Object
     */
    constructor (defaults = {}) {
        if (defaults == null) {
            // this can happen if null is actually passed to the parameter
            defaults = {};
        }

        /**
         * The state of modification for this entity, defaults to New Object
         * @type {EntityStates}
         * @private
         */
        this._State = defaults._State || EntityStates.NewObject;

        /**
         * A GUID that uniquely identifies this entity across domains.
         * @type {string}
         */
        this.UID = defaults.UID || newUID();

        /**
          * The initial, internal name for the entity
          * @type {string}
        */
        this.Name = defaults.Name || "New Entity";
    }

    /**
     * Get the current state of the entity
     * @returns {EntityStates|number}
     * @constructor
     */
    get State () {
        return this._State;
    }

    /**
     * Set this instance of the entity's state to the specified value
     * @param {EntityStates|number} value - The state to set the entity instance to
     */
    setState (value) {
        this._State = value;
    }
}

export class ClientProperties {
    /**
     * Create a client properties object
     * @param {EntityTypes|string} entityType
     * @param {Object} [defaults = {}] - Default object to use for properties
     */
    constructor (entityType, defaults = {}) {
        if (defaults == null) {
            // this can happen if null is actually passed to the parameter
            defaults = {};
        }

        /**
         * The type of entity this client property was created for.
         * @type {EntityTypes}
         */
        this.entityType = entityType;

        /**
         * The dictionary of tags assigned to this entity.
         * @type {Dictionary}
         */
        this.Tags = new Dictionary();

        // apply any extra properties used on this new client properties object
        applyUnknownProperties(this, defaults);
    }

    /**
     * Dispose of in-memory references
     */
    dispose () {
        if (this.Tags == null) {
            return;
        }
        this.Tags.clear();
        this.Tags = null;
    }
}

/**
 * Create an base entity object.
 * @returns {EntityBase}
 */
export function create () {
    return new EntityBase();
}
